import React, { createContext, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../../../../utils/message-type'
import { useGenerateOpinionMutation } from '../../../../../generated/graphql'
import * as Sentry from '@sentry/react-native'
import i18n from '../../../../../i18n'
import llmModelsData from './llm-models.json'

type LLMState = {
  model: string[]
  opinion: string
  loading: boolean
}
interface LLMContextType {
  state: LLMState
  setState: (state: Partial<LLMState>) => void
  generateOpinion: (clusterId: string, prompt: string) => Promise<void>
  getPrompt: (model: string) => string
  generateTitle: (clusterId: string, articleContent: string) => Promise<string>
  getLabel: (model: string) => string
}

const LLMContext = createContext<LLMContextType | undefined>(undefined)

const DefaultPrompt = (language: string) =>
  `The language of the response should be ${language}.\nFormat your response using only basic HTML tags for structure (like <h1>, <p>, <strong>). Do not include <!DOCTYPE>, <html>, <head>, <body> or any metadata tags.`

const DEFAULT_PROMPTS_BY_MODEL = {
  '4.0Ultra': DefaultPrompt,
  '4.0Pro': DefaultPrompt,
  'deepseek-chat': DefaultPrompt,
  'deepseek-reasoner': DefaultPrompt,
}

export const DEFAULT_MODEL = 'deepseek-chat'
export const DEFAULT_LANGUAGE_LABEL = {
  en: 'English',
  zn: 'Chinese',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  ru: 'Русский',
}

export const getDefaultPrompt = (model: string) => {
  const languageCode = i18n.language.split('-')[0]
  const language =
    DEFAULT_LANGUAGE_LABEL[
      languageCode as keyof typeof DEFAULT_LANGUAGE_LABEL
    ] || languageCode

  if (
    !model ||
    !DEFAULT_PROMPTS_BY_MODEL[model as keyof typeof DEFAULT_PROMPTS_BY_MODEL]
  )
    return DefaultPrompt(language)

  return DEFAULT_PROMPTS_BY_MODEL[
    model as keyof typeof DEFAULT_PROMPTS_BY_MODEL
  ](language)
}

export function LLMProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('articles')
  const [state, _setState] = useState({
    model: [DEFAULT_MODEL] as string[],
    opinion: '',
    loading: false,
  })
  const [doGenerateOpinion] = useGenerateOpinionMutation()

  const setState = (state: Partial<LLMState>) => {
    _setState((prevState) => ({ ...prevState, ...state }))
  }

  const generateOpinion = async (clusterId: string, prompt: string) => {
    if (state.loading) return

    if (!prompt.trim()) {
      return Toast.show(t(`The content can't be empty`), MessageType.warn)
    }
    if (state.model.length === 0) {
      return Toast.show(t(`model-required`), MessageType.warn)
    }
    // Temporal Restriction
    if (state.model.length > 1) {
      return Toast.show(t(`only-one-model`), MessageType.warn)
    }
    setState({ loading: true })

    try {
      const opinionResult = await doGenerateOpinion({
        variables: {
          clusterId,
          prompt: prompt,
          model: state.model[0],
        },
      })

      if (!opinionResult.data?.generateOpinion) {
        return Toast.show(t('no-opinion-generated'))
      }
      setState({ opinion: opinionResult.data?.generateOpinion, loading: false })
    } catch (e) {
      Toast.show((e as Error).message, MessageType.error)
      console.error(e)
      Sentry.captureException(e)
    } finally {
      setState({ loading: false })
    }
  }

  const getLabel = (model: string) => {
    const modelData = Object.values(llmModelsData)
      .flatMap((provider) => provider.models)
      .find((m) => m.id === model)
    return modelData ? modelData.label : model
  }

  const generateTitle = async (clusterId: string, articleContent: string) => {
    setState({ loading: true })
    const opinionResult = await doGenerateOpinion({
      variables: {
        clusterId,
        prompt: `Generate a title for the following article: ${articleContent}. The language of the title needs to match the language of the article. Make the title no more than one line. Just return the plain title no any markdown or quotes.`,
        model: state.model[0],
      },
    })
    setState({ loading: false })
    return opinionResult.data?.generateOpinion ?? ''
  }
  return (
    <LLMContext.Provider
      value={{
        state,
        setState,
        generateOpinion,
        getLabel,
        generateTitle,
        getPrompt: (model: string) => getDefaultPrompt(model),
      }}
    >
      {children}
    </LLMContext.Provider>
  )
}

export function useLLMState() {
  const context = useContext(LLMContext)
  if (context === undefined) {
    throw new Error('useLLMState must be used within an LLMProvider')
  }
  return context
}
